import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Sidebar from "~/components/Sidebar";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { All_PENDING_INVOICES_QUERY_KEY } from "~/modules/billing/api/pendingInvoice/pendingInvoiceQueries.ts";
import {
	ALL_PROCESSED_INVOICES_QUERY_KEY,
	PROJECTS_INVOICES_QUERY_KEY,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { Invoice, InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import DeleteInvoiceForm
	from "~/modules/billing/components/InvoiceDetailsView/components/DeleteInvoiceSidebar/components/DeleteInvoiceForm";
import InvoiceIsNotDeletableSidebarContent
	from "~/modules/billing/components/InvoiceDetailsView/components/DeleteInvoiceSidebar/components/InvoiceIsNotDeletableSidebarContent";
import type {
	ValidInvoiceDetailsNavigatedFromOrigin,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import { getInvoiceDetailsBackPath } from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";

type Props = {
	invoiceData: Invoice
	isOpen: boolean;
	onClose: () => void;
	redirectToInvoicesTab?: ValidInvoiceDetailsNavigatedFromOrigin
};

const DeleteInvoiceSidebar = ({
	invoiceData,
	isOpen,
	onClose,
	redirectToInvoicesTab,
}: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const handleSuccess = useCallback(async () => {
		if (invoiceData.projectId) {
			queryClient.invalidateQueries({ queryKey: PROJECTS_INVOICES_QUERY_KEY(invoiceData.projectId) });
		}

		await queryClient.refetchQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });
		await queryClient.refetchQueries({ queryKey: All_PENDING_INVOICES_QUERY_KEY });

		const path = getInvoiceDetailsBackPath(redirectToInvoicesTab, invoiceData.projectId || undefined);

		navigate(path);
	}, [invoiceData.projectId, navigate, queryClient, redirectToInvoicesTab]);

	const deletableStatuses: InvoiceStatusId[] = [invoiceStatusId.Draft, invoiceStatusId.Pending, invoiceStatusId.SentToClient];
	const invoiceIsDeletable = deletableStatuses.includes(invoiceData.invoiceStatusId);

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={onClose}>
			{isOpen && !invoiceIsDeletable && <InvoiceIsNotDeletableSidebarContent onClose={onClose} />}
			{isOpen && invoiceIsDeletable && <DeleteInvoiceForm
				key={isOpen ? "delete-invoice-form" : "delete-invoice-form-closed"}
				onCancel={onClose}
				onSuccess={handleSuccess}
				invoiceData={invoiceData}
			/>}
		</Sidebar>
	);
};

export default DeleteInvoiceSidebar;
